import {
  getFriendlyAction,
  getFriendlyChannel,
  getFriendlyRuleCategory,
  getFriendlyScope,
} from './displayStrings';
import {
  AIRule,
  StatusFilterOption,
  ZoriRulesStatusTab,
  ZoriRulesTab,
} from './types';

export const stubRules: AIRule[] = [
  {
    id: 'rule-stub-1',
    account_id: 'fountainlife',
    channel: 'fountainmembersagent:member-message',
    scope: null,
    condition_description:
      'Member says they ran out of medication or missed doses; alert care team.',
    evaluation_mode: 'llm',
    static_condition: null,
    schedule_delay_hours: null,
    action: 'tool#message_care_team',
    action_params: { urgency: 'high', category: 'medication' },
    visibility: 'opaque',
    status: 'active',
    priority: 10,
    category: 'medication_refill',
    cooldown_seconds: 86400,
    human_description:
      'When a member messages that they have run out of or missed their medication, this rule alerts the care team so they can follow up quickly.',
    flow_mermaid:
      'flowchart TD\n  A[Member sends a chat message] --> B{Out of or missed medication?}\n  B -- Yes --> C[Alert the care team]\n  B -- No --> D[Do nothing]',
    created: new Date().toISOString(),
    created_by: 'clinician@example.com',
    edited_by: 'clinician@example.com',
    last_edited: new Date().toISOString(),
  },
  {
    id: 'rule-stub-2',
    account_id: 'fountainlife',
    channel: 'fountaincliniciansagent:clinician-message',
    scope: 'send_message',
    condition_description:
      'Clinician asks for a summary; execute the executive summary template.',
    evaluation_mode: 'llm',
    static_condition: null,
    schedule_delay_hours: null,
    action: 'tool#execute_template',
    action_params: { template_id: 'executive-summary', audience: 'clinician' },
    visibility: 'transparent',
    status: 'active',
    priority: 7,
    category: null,
    cooldown_seconds: null,
    human_description:
      'When a clinician asks for a summary in chat, this rule runs the executive summary template and sends the result back to them.',
    flow_mermaid:
      'flowchart TD\n  A[Clinician asks for a summary] --> B[Run executive summary template]\n  B --> C[Send summary to the clinician]',
    created: new Date().toISOString(),
    created_by: 'system',
    edited_by: 'system',
    last_edited: new Date().toISOString(),
  },
  {
    id: 'rule-stub-3',
    account_id: 'fountainlife',
    channel: 'patient_change_stream:update',
    scope: 'medication_request',
    condition_description:
      'Medication request changes should be evaluated holistically later.',
    evaluation_mode: 'static',
    static_condition:
      "resourceType == 'MedicationRequest' && action == 'UPDATE'",
    schedule_delay_hours: 6,
    action: 'tool#create_async_task',
    action_params: { agent: 'rule-evaluation-agent' },
    visibility: 'opaque',
    status: 'inactive',
    priority: 5,
    category: 'medication_refill',
    cooldown_seconds: 3600,
    created: new Date().toISOString(),
    created_by: 'system',
    edited_by: 'system',
    last_edited: new Date().toISOString(),
  },
];

export const formatMaybeDateTime = (value: string | null | undefined) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export const formatDateTime = (value: string | number | undefined) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
};

export const getAuditSubjectMemberId = (subjectId: string | null | undefined) =>
  subjectId?.split(':')[0] ?? '';

const normalizeSearchText = (value: string) =>
  value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * Case- and punctuation-insensitive multi-term match. Each whitespace-separated
 * term in the query must appear as a substring of the normalized candidate, so
 * "member updated" matches but unrelated rules that merely share characters do
 * not. Kept stricter than the document matcher subsequence search because rule
 * conditions are long and subsequence matching over-matches.
 */
export const searchTextMatches = (
  candidate: string,
  query: string,
): boolean => {
  const normalizedCandidate = normalizeSearchText(candidate);
  return query
    .split(/\s+/)
    .map(normalizeSearchText)
    .filter(Boolean)
    .every(term => normalizedCandidate.includes(term));
};

/**
 * Concatenated, searchable representation of a rule. Includes both raw machine
 * values (id, channel, scope, action) and their friendly labels so users can
 * cmd-f style search by either the technical token or the human label.
 */
export const getRuleSearchText = (rule: AIRule): string =>
  [
    rule.id,
    rule.channel,
    getFriendlyChannel(rule.channel),
    rule.scope,
    getFriendlyScope(rule.scope),
    rule.action,
    getFriendlyAction(rule.action),
    rule.condition_description,
    rule.static_condition,
    rule.category,
    getFriendlyRuleCategory(rule.category),
    rule.account_id,
    rule.visibility,
    rule.status,
  ]
    .filter(Boolean)
    .join(' ');

/** Filters rules by a free-text query across id, channel, scope, action, and condition. */
export const filterRulesBySearch = (
  rules: AIRule[],
  searchQuery: string,
): AIRule[] => {
  const trimmed = searchQuery.trim();
  if (!trimmed) {
    return rules;
  }
  return rules.filter(rule =>
    searchTextMatches(getRuleSearchText(rule), trimmed),
  );
};

export const getUseStubRules = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('stubRules') === '1';
  } catch {
    return false;
  }
};

export const normalizeJson = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/** Wraps a FHIRPath static rule condition for read-only markdown display. */
export const formatStaticConditionMarkdown = (condition: string): string => {
  const trimmed = condition.trim();
  if (!trimmed) {
    return '';
  }
  const fence = trimmed.includes('```') ? '````' : '```';
  return `${fence}\n${trimmed}\n${fence}`;
};

export type RuleFormValidationError =
  | 'condition_required'
  | 'priority_invalid'
  | 'static_condition_required'
  | 'action_params_invalid'
  | 'schedule_delay_invalid'
  | 'cooldown_invalid';

export interface RuleFormValidationInput {
  conditionDescription: string;
  priority: string;
  evaluationMode: 'llm' | 'static';
  staticCondition: string;
  actionParamsJson: string;
  scheduleDelayHours: string;
  cooldownSeconds: string;
}

export const validateRuleForm = (
  input: RuleFormValidationInput,
): RuleFormValidationError | null => {
  if (input.evaluationMode === 'llm' && !input.conditionDescription.trim()) {
    return 'condition_required';
  }
  const parsedPriority = Number(input.priority);
  if (
    Number.isNaN(parsedPriority) ||
    parsedPriority < 1 ||
    parsedPriority > 10
  ) {
    return 'priority_invalid';
  }
  if (input.evaluationMode === 'static' && !input.staticCondition.trim()) {
    return 'static_condition_required';
  }
  try {
    JSON.parse(input.actionParamsJson || '{}');
  } catch {
    return 'action_params_invalid';
  }

  const scheduleDelay =
    input.scheduleDelayHours.trim() === ''
      ? null
      : Number(input.scheduleDelayHours);
  if (scheduleDelay !== null && Number.isNaN(scheduleDelay)) {
    return 'schedule_delay_invalid';
  }

  const cooldownSeconds =
    input.cooldownSeconds.trim() === '' ? null : Number(input.cooldownSeconds);
  if (cooldownSeconds !== null && Number.isNaN(cooldownSeconds)) {
    return 'cooldown_invalid';
  }

  return null;
};

export const statusFilterByTab: Record<ZoriRulesStatusTab, StatusFilterOption> =
  {
    all: 'ALL',
    active: 'ACTIVE',
    inactive: 'INACTIVE',
  };

export type ZoriRulesSectionTab = Extract<
  ZoriRulesTab,
  | 'rules'
  | 'knowledge-base'
  | 'document-types'
  | 'guidance'
  | 'tasks'
  | 'rule-actions'
  | 'document-matchers'
>;

export const getZoriRulesSectionTab = (
  tab: string | undefined,
): ZoriRulesSectionTab => {
  if (
    tab === 'knowledge-base' ||
    tab === 'document-types' ||
    tab === 'guidance' ||
    tab === 'tasks' ||
    tab === 'rule-actions' ||
    tab === 'document-matchers'
  ) {
    return tab;
  }
  return 'rules';
};

export const getZoriRulesStatusTab = (
  tab: string | undefined,
): ZoriRulesStatusTab => {
  if (tab === 'active' || tab === 'inactive') {
    return tab;
  }
  return 'all';
};

export async function fetchAuthedJson<T>(
  path: string,
  authToken: string,
  accountId: string,
): Promise<T> {
  return mutateAuthedJson<T>({
    path,
    authToken,
    accountId,
    method: 'GET',
  });
}

export async function mutateAuthedJson<T>({
  path,
  authToken,
  accountId,
  method,
  body,
}: {
  path: string;
  authToken: string;
  accountId: string;
  method: 'GET' | 'PUT' | 'DELETE' | 'POST' | 'PATCH';
  body?: unknown;
}): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${authToken}`,
      'LifeOmic-Account': accountId,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

export async function fetchAuditRows<T>(
  path: string,
  authToken: string,
  accountId: string,
): Promise<T[]> {
  return fetchAuthedJson<T[]>(path, authToken, accountId);
}
